import { Link, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleBody from '@/pages/article/components/ArticleBody';
import ArticleToc from '@/pages/article/components/ArticleToc';
import RelatedArticles from '@/pages/article/components/RelatedArticles';
import { articles, getArticleBySlug, siteInfo } from '@/mocks/gossip';

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const article = getArticleBySlug(slug);

  const tocItems = useMemo(
    () =>
      (article?.sections || [])
        .map((section, index) => ({ id: `section-${index}`, text: section.heading || '' }))
        .filter((item) => item.text),
    [article],
  );

  const related = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((item) => article.related.includes(item.slug) || item.category === article.category)
      .filter((item) => item.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  const index = articles.findIndex((item) => item.slug === slug);
  const prevArticle = index > 0 ? articles[index - 1] : null;
  const nextArticle = index >= 0 && index < articles.length - 1 ? articles[index + 1] : null;

  const hotList = useMemo(
    () => [...articles].filter((item) => item.slug !== slug).slice(0, 4),
    [slug],
  );

  if (!article) {
    return (
      <>
        <Seo
          title="内容不存在 - 诱惑吃瓜"
          description="你访问的内容暂时无法查看，可以返回首页浏览其他图集。"
          robots="noindex, follow"
        />
        <div className="mx-auto max-w-[1240px] px-4 py-24 text-center md:px-8">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
            <i className="ri-file-search-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这篇内容</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经调整，或者链接输入有误。</p>
          <Link
            to="/category/all"
            className="mt-6 inline-flex h-11 items-center gap-1 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回全部图集
          </Link>
        </div>
      </>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.youhuochigua.com';

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      datePublished: article.date,
      dateModified: article.date,
      articleSection: article.categoryName,
      inLanguage: 'zh-CN',
      author: { '@type': 'Person', name: article.author },
      publisher: {
        '@type': 'Organization',
        name: siteInfo.name,
        url: origin,
      },
      mainEntityOfPage: `${origin}/article/${article.slug}`,
      keywords: article.tags.join(','),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: origin },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.categoryName,
          item: `${origin}/category/${article.category}`,
        },
        { '@type': 'ListItem', position: 3, name: article.title, item: `${origin}/article/${article.slug}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} - 诱惑吃瓜`}
        description={article.excerpt.slice(0, 150)}
        keywords={`${article.tags.join(',')},诱惑吃瓜,${article.categoryName}`}
        type="article"
        image={article.image}
        jsonLd={structuredData}
      />

      <Breadcrumbs
        items={[
          { label: '首页', path: '/' },
          { label: article.categoryName, path: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article>
        <header className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.image}
              alt={article.alt}
              title={`${article.title} - 诱惑吃瓜`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/70 to-foreground-950/45" />
          </div>
          <div className="relative mx-auto flex max-w-[1240px] flex-col justify-end px-4 py-14 md:px-8 md:py-20">
            <Link
              to={`/category/${article.category}`}
              className="flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3.5 py-1 text-[11px] font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-bookmark-3-line text-sm" aria-hidden="true"></i>
              {article.categoryName}
            </Link>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug tracking-wide text-background-50 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200/85 md:text-base">
              {article.excerpt}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-200/80 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line text-base" aria-hidden="true"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-base" aria-hidden="true"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-base" aria-hidden="true"></i>
                阅读约 {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-base" aria-hidden="true"></i>
                {article.views} 阅读
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1240px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <ArticleBody sections={article.sections} />

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
                  {article.author.slice(0, 1)}
                </span>
                <div>
                  <p className="font-heading text-base font-semibold text-foreground-950">
                    {article.author}
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">
                    诱惑吃瓜内容编辑，长期跟进红毯现场与造型解析，关注画面里的每一处细节。
                  </p>
                </div>
              </div>

              <nav aria-label="文章翻页" className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {prevArticle ? (
                  <Link
                    to={`/article/${prevArticle.slug}`}
                    className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-arrow-left-line text-sm" aria-hidden="true"></i>
                      上一篇
                    </span>
                    <p className="mt-2 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {prevArticle.title}
                    </p>
                  </Link>
                ) : (
                  <span />
                )}
                {nextArticle && (
                  <Link
                    to={`/article/${nextArticle.slug}`}
                    className="group rounded-lg border border-background-200 bg-background-50 p-4 text-right transition-colors hover:border-primary-300 sm:col-start-2"
                  >
                    <span className="flex items-center justify-end gap-1 text-xs text-foreground-500">
                      下一篇
                      <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
                    </span>
                    <p className="mt-2 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {nextArticle.title}
                    </p>
                  </Link>
                )}
              </nav>
            </div>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-6 lg:sticky lg:top-20">
                <ArticleToc items={tocItems} />

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-book-open-line text-lg text-accent-600" aria-hidden="true"></i>
                    更多值得一看
                  </h2>
                  <div className="mt-3 flex flex-col gap-4">
                    {hotList.map((item) => (
                      <Link
                        key={item.id}
                        to={`/article/${item.slug}`}
                        className="group flex gap-3"
                      >
                        <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md">
                          <img
                            src={item.image}
                            alt={item.alt}
                            title={`${item.title} - 诱惑吃瓜`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </p>
                          <p className="mt-1 text-xs text-foreground-500">{item.categoryName}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg bg-primary-950 p-5 text-background-100">
                  <h2 className="font-heading text-base font-bold text-background-50">
                    想看哪组同框？
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-background-200/80">
                    告诉我们你关注的对象与场次，编辑团队会优先整理相关图集。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 flex h-10 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-accent-500 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
                    提交需求
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <RelatedArticles items={related} />
    </>
  );
}